/**
 * Wave 28: 10 new cities
 * Toulouse 🇫🇷, Stuttgart 🇩🇪, Poznań 🇵🇱, Timișoara 🇷🇴, Kaunas 🇱🇹,
 * Coimbatore 🇮🇳, Fukuoka 🇯🇵, Quito Hub2 🇪🇨, Windhoek 🇳🇦, Sharjah 🇦🇪
 */
import type { CityAgencyData } from './city-agency-pages';

export const CITY_AGENCY_PAGES_WAVE28: Record<string, CityAgencyData> = {
  toulouse: {
    slug: 'software-development-agency-toulouse',
    cityName: 'Toulouse',
    countryName: 'France',
    flag: '🇫🇷',
    timezone: 'CET (UTC+1)',
    currency: 'EUR',
    title: { en: 'Software Development Agency Toulouse | Call IT Dev', fr: 'Agence Développement Web Toulouse | Call IT Dev' },
    description: { en: 'Top software development agency in Toulouse. Aerospace-grade engineering, SaaS, mobile apps & AI solutions. From $25/hr vs €600/day local.', fr: 'Agence de développement logiciel à Toulouse. Ingénierie aérospatiale, SaaS, apps mobiles & IA. Dès 25$/h vs 600€/jour local.' },
    keywords: ['software development Toulouse', 'agence développement Toulouse', 'développeur Toulouse', 'IT outsourcing Toulouse'],
    heroTitle: { en: 'Software Development Agency in Toulouse', fr: 'Agence de Développement Logiciel à Toulouse' },
    heroSubtitle: { en: 'Aerospace-grade software engineering at nearshore rates. 70% savings vs local Toulouse agencies.', fr: 'Ingénierie logicielle de qualité aérospatiale à tarif nearshore. 70% d\'économies vs agences locales.' },
    whyTitle: { en: 'Why Choose Us Over Toulouse Agencies', fr: 'Pourquoi Nous Choisir vs Agences Toulousaines' },
    whyReasons: [
      { icon: '🚀', title: 'Aerospace DNA', description: 'Engineering rigor meeting startup agility for Toulouse\'s tech ecosystem.' },
      { icon: '💰', title: '70% Cost Savings', description: 'Senior developers from $25/hr vs €500-700/day in Toulouse.' },
      { icon: '🕐', title: 'Same Timezone', description: 'CET-aligned teams for real-time collaboration with your Toulouse office.' },
      { icon: '🇫🇷', title: 'French-Speaking', description: 'Native French developers who understand your business culture.' },
    ],
    services: [
      { name: 'SaaS Development', description: 'Custom SaaS platforms for aerospace, health & fintech verticals.' },
      { name: 'Mobile Apps', description: 'iOS & Android apps with Flutter and React Native.' },
      { name: 'AI & ML', description: 'Computer vision, NLP and predictive analytics solutions.' },
      { name: 'Cloud & DevOps', description: 'AWS, Azure & GCP infrastructure automation.' },
    ],
    industries: [
      { name: 'Aerospace', description: 'Software for Airbus suppliers and aerospace startups.' },
      { name: 'HealthTech', description: 'HIPAA-compliant health platforms and telemedicine.' },
      { name: 'AgriTech', description: 'Precision agriculture and supply chain platforms.' },
    ],
    faqs: [
      { question: 'How do you compare to Toulouse agencies?', answer: 'We deliver the same quality at 70% lower cost with CET-timezone alignment and native French communication.' },
      { question: 'Do you work with Toulouse startups?', answer: 'Yes, we support 50+ startups across France including aerospace and deep tech ventures.' },
    ],
    localRate: '€500-700/day',
    ourRate: '$25-45/hr',
    savings: '65-75%',
  },

  stuttgart: {
    slug: 'software-development-agency-stuttgart',
    cityName: 'Stuttgart',
    countryName: 'Germany',
    flag: '🇩🇪',
    timezone: 'CET (UTC+1)',
    currency: 'EUR',
    title: { en: 'Software Development Agency Stuttgart | Call IT Dev', de: 'Softwareentwicklung Stuttgart | Call IT Dev' },
    description: { en: 'Premium software development in Stuttgart. Automotive-grade engineering, Industry 4.0, IoT & AI. From $25/hr vs €700/day local.', de: 'Premium-Softwareentwicklung Stuttgart. Automotive-Engineering, Industrie 4.0, IoT & KI. Ab 25$/h vs 700€/Tag lokal.' },
    keywords: ['software development Stuttgart', 'Softwareentwicklung Stuttgart', 'IT outsourcing Stuttgart', 'app development Stuttgart'],
    heroTitle: { en: 'Software Development Agency in Stuttgart', de: 'Softwareentwicklungsagentur in Stuttgart' },
    heroSubtitle: { en: 'Automotive-grade software at nearshore prices. 70% savings vs Stuttgart rates.', de: 'Automotive-Softwarequalität zu Nearshore-Preisen. 70% Ersparnis vs Stuttgart.' },
    whyTitle: { en: 'Why Choose Us Over Stuttgart Agencies', de: 'Warum uns statt Stuttgarter Agenturen' },
    whyReasons: [
      { icon: '🏭', title: 'Industry 4.0', description: 'Smart factory software for automotive & manufacturing sectors.' },
      { icon: '💰', title: '70% Savings', description: 'Senior developers from $25/hr vs €600-800/day locally.' },
      { icon: '🕐', title: 'CET Timezone', description: 'Same working hours as your Stuttgart headquarters.' },
      { icon: '🔧', title: 'Engineering Excellence', description: 'German-standard engineering with agile methodology.' },
    ],
    services: [
      { name: 'IoT & Industry 4.0', description: 'Connected factory and smart manufacturing platforms.' },
      { name: 'Automotive Software', description: 'Embedded systems, fleet management, and connected car apps.' },
      { name: 'ERP Integration', description: 'SAP integration and custom enterprise resource planning.' },
      { name: 'AI & Data', description: 'Predictive maintenance and quality analytics.' },
    ],
    industries: [
      { name: 'Automotive', description: 'Software for Daimler, Porsche and Bosch suppliers.' },
      { name: 'Manufacturing', description: 'Industry 4.0 digitalization and process automation.' },
      { name: 'Engineering', description: 'CAD/CAM integration and simulation tools.' },
    ],
    faqs: [
      { question: 'Do you work with German automotive companies?', answer: 'Yes, we support automotive suppliers and OEMs across Baden-Württemberg with Industry 4.0 solutions.' },
      { question: 'Can you integrate with SAP?', answer: 'Absolutely. We have SAP-certified developers experienced with S/4HANA, BTP, and ABAP.' },
    ],
    localRate: '€600-800/day',
    ourRate: '$25-45/hr',
    savings: '70-75%',
  },

  poznan: {
    slug: 'software-development-agency-poznan',
    cityName: 'Poznań',
    countryName: 'Poland',
    flag: '🇵🇱',
    timezone: 'CET (UTC+1)',
    currency: 'PLN',
    title: { en: 'Software Development Agency Poznań | Call IT Dev', pl: 'Agencja Programistyczna Poznań | Call IT Dev' },
    description: { en: 'Software development agency Poznań. Full-stack, SaaS, mobile & AI. From $25/hr vs 800 PLN/day local.', pl: 'Agencja programistyczna Poznań. Full-stack, SaaS, mobile & AI. Od 25$/h vs 800 PLN/dzień lokalnie.' },
    keywords: ['software development Poznań', 'agencja IT Poznań', 'programiści Poznań'],
    heroTitle: { en: 'Software Development Agency in Poznań', pl: 'Agencja Programistyczna w Poznaniu' },
    heroSubtitle: { en: 'High-quality development at competitive rates. 50% savings vs Poznań agencies.', pl: 'Wysokiej jakości rozwój w konkurencyjnych cenach. 50% oszczędności vs agencje poznańskie.' },
    whyTitle: { en: 'Why Choose Us Over Poznań Agencies', pl: 'Dlaczego My vs Agencje Poznańskie' },
    whyReasons: [
      { icon: '🎓', title: 'Top Talent', description: 'Access to 500+ skilled developers across all tech stacks.' },
      { icon: '💰', title: '50% Savings', description: 'Competitive rates even vs Polish nearshore prices.' },
      { icon: '🕐', title: 'CET Aligned', description: 'Same timezone for seamless daily collaboration.' },
      { icon: '🚀', title: 'Fast Delivery', description: '2-week sprint cycles with transparent progress tracking.' },
    ],
    services: [
      { name: 'Full-Stack Development', description: 'React, Node.js, Python, and .NET solutions.' },
      { name: 'SaaS Platforms', description: 'Multi-tenant SaaS with subscription management.' },
      { name: 'Mobile Apps', description: 'Cross-platform mobile development with Flutter.' },
      { name: 'QA & Testing', description: 'Automated testing and quality assurance services.' },
    ],
    industries: [
      { name: 'E-commerce', description: 'Shopify Plus, WooCommerce and custom e-commerce.' },
      { name: 'FinTech', description: 'Payment processing and banking platforms.' },
      { name: 'EdTech', description: 'Learning management systems and e-learning.' },
    ],
    faqs: [
      { question: 'How do you compare to Poznań-based agencies?', answer: 'We offer comparable or better quality at 40-50% lower rates with the same CET timezone advantage.' },
      { question: 'What technologies do you specialize in?', answer: 'React, Node.js, Python, Flutter, AWS, and Azure across 500+ developers.' },
    ],
    localRate: '700-900 PLN/day',
    ourRate: '$25-40/hr',
    savings: '45-55%',
  },

  timisoara: {
    slug: 'software-development-agency-timisoara',
    cityName: 'Timișoara',
    countryName: 'Romania',
    flag: '🇷🇴',
    timezone: 'EET (UTC+2)',
    currency: 'RON',
    title: { en: 'Software Development Agency Timișoara | Call IT Dev' },
    description: { en: 'Software development agency in Timișoara. Full-stack, cloud, AI & IoT. From $25/hr vs €400/day local rates.' },
    keywords: ['software development Timișoara', 'IT outsourcing Romania', 'developers Timișoara'],
    heroTitle: { en: 'Software Development Agency in Timișoara' },
    heroSubtitle: { en: 'Romania\'s tech hub excellence at even more competitive nearshore rates. 55% savings vs local.' },
    whyTitle: { en: 'Why Choose Us Over Timișoara Agencies' },
    whyReasons: [
      { icon: '🏆', title: 'Tech Hub', description: 'Leveraging Romania\'s strongest IT ecosystem.' },
      { icon: '💰', title: '55% Savings', description: 'Senior devs from $25/hr vs €350-500/day locally.' },
      { icon: '🕐', title: 'EET Timezone', description: 'Just 1 hour ahead of CET for EU clients.' },
      { icon: '🎯', title: 'EU Quality', description: 'EU-standard development processes and GDPR compliance.' },
    ],
    services: [
      { name: 'Cloud Native', description: 'Microservices, Kubernetes and serverless architectures.' },
      { name: 'AI & ML', description: 'Machine learning models and data pipelines.' },
      { name: 'Enterprise Software', description: 'Custom ERP, CRM and workflow automation.' },
      { name: 'DevOps', description: 'CI/CD pipelines and infrastructure as code.' },
    ],
    industries: [
      { name: 'Automotive', description: 'Connected car and fleet management software.' },
      { name: 'Healthcare', description: 'HIPAA/GDPR-compliant health platforms.' },
      { name: 'Logistics', description: 'Supply chain optimization and tracking systems.' },
    ],
    faqs: [
      { question: 'Why choose you over Romanian agencies?', answer: 'We combine Romanian-level quality with even lower rates and 24/7 support capability.' },
      { question: 'Are you GDPR compliant?', answer: 'Yes, fully GDPR-aligned with EU data protection standards.' },
    ],
    localRate: '€350-500/day',
    ourRate: '$25-40/hr',
    savings: '50-60%',
  },

  kaunas: {
    slug: 'software-development-agency-kaunas',
    cityName: 'Kaunas',
    countryName: 'Lithuania',
    flag: '🇱🇹',
    timezone: 'EET (UTC+2)',
    currency: 'EUR',
    title: { en: 'Software Development Agency Kaunas | Call IT Dev' },
    description: { en: 'Software development agency Kaunas, Lithuania. FinTech, SaaS, AI & blockchain solutions. From $25/hr vs €450/day local.' },
    keywords: ['software development Kaunas', 'IT outsourcing Lithuania', 'developers Kaunas'],
    heroTitle: { en: 'Software Development Agency in Kaunas' },
    heroSubtitle: { en: 'Baltic fintech excellence at nearshore rates. 60% savings vs Kaunas agencies.' },
    whyTitle: { en: 'Why Choose Us Over Kaunas Agencies' },
    whyReasons: [
      { icon: '🏦', title: 'FinTech Focus', description: 'Deep expertise in banking, payments and blockchain.' },
      { icon: '💰', title: '60% Savings', description: 'Senior developers from $25/hr vs €400-500/day.' },
      { icon: '🕐', title: 'EET Timezone', description: 'Baltic timezone alignment for EU collaboration.' },
      { icon: '🔒', title: 'Security First', description: 'PCI-DSS ready and GDPR-aligned development.' },
    ],
    services: [
      { name: 'FinTech Development', description: 'Payment gateways, neobanking and lending platforms.' },
      { name: 'Blockchain', description: 'Smart contracts, DeFi and tokenization solutions.' },
      { name: 'SaaS', description: 'Multi-tenant SaaS with European hosting.' },
      { name: 'Cybersecurity', description: 'Penetration testing and security audits.' },
    ],
    industries: [
      { name: 'Banking', description: 'Digital banking and payment processing platforms.' },
      { name: 'InsurTech', description: 'Insurance automation and claims management.' },
      { name: 'RegTech', description: 'Compliance automation and AML solutions.' },
    ],
    faqs: [
      { question: 'Do you have FinTech experience?', answer: 'Yes, we\'ve delivered 40+ fintech projects including neobanks, payment gateways, and crypto platforms.' },
      { question: 'Can you handle PCI-DSS requirements?', answer: 'Absolutely. Our development follows PCI-DSS ready standards for payment processing.' },
    ],
    localRate: '€400-500/day',
    ourRate: '$25-40/hr',
    savings: '55-65%',
  },

  coimbatore: {
    slug: 'software-development-agency-coimbatore',
    cityName: 'Coimbatore',
    countryName: 'India',
    flag: '🇮🇳',
    timezone: 'IST (UTC+5:30)',
    currency: 'INR',
    title: { en: 'Software Development Agency Coimbatore | Call IT Dev' },
    description: { en: 'Software development agency Coimbatore. Full-stack, AI, mobile & cloud solutions. From $15/hr vs ₹5000/day local.' },
    keywords: ['software development Coimbatore', 'IT company Coimbatore', 'app development Coimbatore'],
    heroTitle: { en: 'Software Development Agency in Coimbatore' },
    heroSubtitle: { en: 'South India\'s emerging tech hub. Enterprise-grade software at the best global rates.' },
    whyTitle: { en: 'Why Choose Us for Coimbatore Projects' },
    whyReasons: [
      { icon: '🏭', title: 'Manufacturing Hub', description: 'Software solutions for India\'s industrial capital.' },
      { icon: '💰', title: 'Best Value', description: 'Senior developers from $15/hr with Indian market expertise.' },
      { icon: '🌏', title: 'Global Reach', description: '12+ language support for international clients.' },
      { icon: '📱', title: 'Mobile First', description: 'India-optimized mobile apps and progressive web apps.' },
    ],
    services: [
      { name: 'ERP Solutions', description: 'Custom ERP for manufacturing and textiles industry.' },
      { name: 'Mobile Apps', description: 'Android-first development for Indian market.' },
      { name: 'E-commerce', description: 'Multi-vendor marketplaces and D2C platforms.' },
      { name: 'AI & Automation', description: 'Process automation and predictive analytics.' },
    ],
    industries: [
      { name: 'Manufacturing', description: 'Industry 4.0 for textile and auto parts manufacturing.' },
      { name: 'Healthcare', description: 'Hospital management and telemedicine platforms.' },
      { name: 'Education', description: 'EdTech platforms and LMS solutions.' },
    ],
    faqs: [
      { question: 'Do you serve Coimbatore businesses?', answer: 'Yes, we support businesses in Coimbatore with tailored software for manufacturing, textiles, and services.' },
      { question: 'What are your rates vs Coimbatore agencies?', answer: 'We offer comparable rates with access to a larger talent pool and 24/7 support.' },
    ],
    localRate: '₹4000-6000/day',
    ourRate: '$15-30/hr',
    savings: '40-50%',
  },

  fukuoka: {
    slug: 'software-development-agency-fukuoka',
    cityName: 'Fukuoka',
    countryName: 'Japan',
    flag: '🇯🇵',
    timezone: 'JST (UTC+9)',
    currency: 'JPY',
    title: { en: 'Software Development Agency Fukuoka | Call IT Dev', ja: 'ソフトウェア開発会社 福岡 | Call IT Dev' },
    description: { en: 'Software development agency Fukuoka. SaaS, mobile, AI & startup solutions. From $25/hr vs ¥80,000/day local.', ja: '福岡のソフトウェア開発会社。SaaS、モバイル、AI＆スタートアップ。25$/時〜 vs 現地8万円/日。' },
    keywords: ['software development Fukuoka', 'ソフトウェア開発 福岡', 'IT outsourcing Fukuoka'],
    heroTitle: { en: 'Software Development Agency in Fukuoka', ja: '福岡のソフトウェア開発エージェンシー' },
    heroSubtitle: { en: 'Japan\'s startup city meets global nearshore excellence. 65% savings vs Fukuoka rates.', ja: '日本のスタートアップシティとグローバルニアショアの融合。65%コスト削減。' },
    whyTitle: { en: 'Why Choose Us Over Fukuoka Agencies', ja: 'なぜ福岡の代理店より私たちを選ぶのか' },
    whyReasons: [
      { icon: '🚀', title: 'Startup Friendly', description: 'Agile development for Fukuoka\'s vibrant startup scene.' },
      { icon: '💰', title: '65% Savings', description: 'Senior developers from $25/hr vs ¥70,000-90,000/day.' },
      { icon: '🇯🇵', title: 'Japan Ready', description: 'Japanese-speaking project managers and localized UX.' },
      { icon: '⚡', title: 'Fast Launch', description: 'MVP in 4-6 weeks with iterative development.' },
    ],
    services: [
      { name: 'Startup MVPs', description: 'Rapid prototyping and minimum viable products.' },
      { name: 'SaaS Development', description: 'Cloud-native SaaS platforms with Japanese localization.' },
      { name: 'Mobile Apps', description: 'iOS and Android apps optimized for the Japanese market.' },
      { name: 'AI & Data', description: 'Machine learning models and data analytics.' },
    ],
    industries: [
      { name: 'Gaming', description: 'Game backends and social gaming platforms.' },
      { name: 'E-commerce', description: 'Japanese marketplace integrations and D2C.' },
      { name: 'HealthTech', description: 'Telemedicine and health management apps.' },
    ],
    faqs: [
      { question: 'Do you support Japanese language projects?', answer: 'Yes, we have Japanese-speaking PMs and full localization capability for the Japanese market.' },
      { question: 'Can you build MVPs for Fukuoka startups?', answer: 'Absolutely. We specialize in 4-6 week MVPs at 65% less than local agency costs.' },
    ],
    localRate: '¥70,000-90,000/day',
    ourRate: '$25-45/hr',
    savings: '60-70%',
  },

  quitoHub2: {
    slug: 'software-development-agency-quito-hub2',
    cityName: 'Quito Hub 2',
    countryName: 'Ecuador',
    flag: '🇪🇨',
    timezone: 'ECT (UTC-5)',
    currency: 'USD',
    title: { en: 'Software Development Agency Quito | Call IT Dev', es: 'Agencia Desarrollo Software Quito | Call IT Dev' },
    description: { en: 'Software development agency Quito, Ecuador. Full-stack, SaaS, mobile & cloud. From $20/hr vs $60/hr local.', es: 'Agencia desarrollo software Quito, Ecuador. Full-stack, SaaS, mobile & cloud. Desde $20/hr vs $60/hr local.' },
    keywords: ['software development Quito', 'desarrollo software Quito', 'agencia IT Quito'],
    heroTitle: { en: 'Software Development Agency in Quito', es: 'Agencia de Desarrollo de Software en Quito' },
    heroSubtitle: { en: 'Americas-aligned development at unbeatable rates. 60% savings vs local Quito agencies.', es: 'Desarrollo alineado con las Américas a tarifas imbatibles. 60% de ahorro vs agencias locales.' },
    whyTitle: { en: 'Why Choose Us Over Quito Agencies', es: 'Por qué elegirnos vs agencias de Quito' },
    whyReasons: [
      { icon: '🌎', title: 'US-Aligned', description: 'EST/CST timezone for North American clients.' },
      { icon: '💰', title: '60% Savings', description: 'Senior developers from $20/hr vs $50-70/hr locally.' },
      { icon: '🇪🇸', title: 'Spanish Native', description: 'Native Spanish developers for LATAM markets.' },
      { icon: '💵', title: 'USD Economy', description: 'No currency risk — Ecuador uses US dollars.' },
    ],
    services: [
      { name: 'Full-Stack Development', description: 'React, Node.js, Python and Django solutions.' },
      { name: 'SaaS Platforms', description: 'Multi-tenant SaaS for LATAM and US markets.' },
      { name: 'Mobile Apps', description: 'Cross-platform apps with Flutter and React Native.' },
      { name: 'Cloud Migration', description: 'AWS and Azure cloud migration services.' },
    ],
    industries: [
      { name: 'Oil & Gas', description: 'Energy sector digitalization and IoT monitoring.' },
      { name: 'Agriculture', description: 'AgriTech platforms and precision farming tools.' },
      { name: 'Tourism', description: 'Booking platforms and travel tech solutions.' },
    ],
    faqs: [
      { question: 'Why use USD pricing?', answer: 'Ecuador\'s dollarized economy eliminates currency risk for US and international clients.' },
      { question: 'Do you cover the Americas timezone?', answer: 'Yes, our teams operate in EST-PST windows for full Americas coverage.' },
    ],
    localRate: '$50-70/hr',
    ourRate: '$20-35/hr',
    savings: '55-65%',
  },

  windhoek: {
    slug: 'software-development-agency-windhoek',
    cityName: 'Windhoek',
    countryName: 'Namibia',
    flag: '🇳🇦',
    timezone: 'CAT (UTC+2)',
    currency: 'NAD',
    title: { en: 'Software Development Agency Windhoek | Call IT Dev' },
    description: { en: 'Software development agency Windhoek, Namibia. Full-stack, mobile, cloud & AI. From $20/hr. Africa\'s emerging tech hub.' },
    keywords: ['software development Windhoek', 'IT company Namibia', 'developers Windhoek'],
    heroTitle: { en: 'Software Development Agency in Windhoek' },
    heroSubtitle: { en: 'Africa\'s stable tech gateway. Enterprise-grade development for Southern Africa.' },
    whyTitle: { en: 'Why Choose Us for Windhoek Projects' },
    whyReasons: [
      { icon: '🌍', title: 'Africa Gateway', description: 'Strategic entry point for Southern African markets.' },
      { icon: '💰', title: 'Best Value', description: 'Senior developers from $20/hr for African projects.' },
      { icon: '🏛️', title: 'Stable Market', description: 'Namibia\'s stable economy and business-friendly policies.' },
      { icon: '🔒', title: 'POPIA Ready', description: 'Compliant with Southern African data protection laws.' },
    ],
    services: [
      { name: 'Government Tech', description: 'E-government and public service digitalization.' },
      { name: 'Mobile Apps', description: 'Mobile-first solutions for African markets.' },
      { name: 'Fintech', description: 'Mobile money and digital banking platforms.' },
      { name: 'Mining Tech', description: 'Mining operations management and IoT monitoring.' },
    ],
    industries: [
      { name: 'Mining', description: 'Digital solutions for diamond and uranium mining.' },
      { name: 'Tourism', description: 'Safari booking and ecotourism platforms.' },
      { name: 'Government', description: 'E-governance and citizen services portals.' },
    ],
    faqs: [
      { question: 'Do you serve Namibian businesses?', answer: 'Yes, we support businesses across Southern Africa with locally-adapted digital solutions.' },
      { question: 'What about data protection?', answer: 'We follow POPIA and GDPR standards for all Southern African projects.' },
    ],
    localRate: 'N$3,000-5,000/day',
    ourRate: '$20-35/hr',
    savings: '50-60%',
  },

  sharjah: {
    slug: 'software-development-agency-sharjah',
    cityName: 'Sharjah',
    countryName: 'UAE',
    flag: '🇦🇪',
    timezone: 'GST (UTC+4)',
    currency: 'AED',
    title: { en: 'Software Development Agency Sharjah | Call IT Dev', ar: 'شركة تطوير برمجيات الشارقة | Call IT Dev' },
    description: { en: 'Software development agency Sharjah, UAE. SaaS, mobile, AI & enterprise solutions. From $25/hr vs AED 2,500/day local.', ar: 'شركة تطوير برمجيات في الشارقة، الإمارات. SaaS، موبايل، ذكاء اصطناعي. من 25$/ساعة.' },
    keywords: ['software development Sharjah', 'IT company Sharjah', 'تطوير برمجيات الشارقة', 'app development UAE'],
    heroTitle: { en: 'Software Development Agency in Sharjah', ar: 'شركة تطوير برمجيات في الشارقة' },
    heroSubtitle: { en: 'UAE enterprise software at nearshore rates. 65% savings vs local Sharjah agencies.', ar: 'برمجيات المؤسسات الإماراتية بأسعار نيرشور. وفورات 65% مقارنة بالشارقة.' },
    whyTitle: { en: 'Why Choose Us Over Sharjah Agencies', ar: 'لماذا تختارنا بدلاً من وكالات الشارقة' },
    whyReasons: [
      { icon: '🏙️', title: 'UAE Expertise', description: 'Deep understanding of UAE business culture and regulations.' },
      { icon: '💰', title: '65% Savings', description: 'Senior developers from $25/hr vs AED 2,000-3,000/day.' },
      { icon: '🕐', title: 'GST Aligned', description: 'Just 2 hours behind UAE for real-time collaboration.' },
      { icon: '🌐', title: 'Arabic Native', description: 'Native Arabic developers and RTL-ready interfaces.' },
    ],
    services: [
      { name: 'Enterprise Software', description: 'Custom ERP, CRM and business automation.' },
      { name: 'E-commerce', description: 'Arabic-first e-commerce and marketplace platforms.' },
      { name: 'Mobile Apps', description: 'iOS & Android apps for the Gulf market.' },
      { name: 'Government Tech', description: 'Smart city and e-government solutions.' },
    ],
    industries: [
      { name: 'Real Estate', description: 'Property management and PropTech platforms.' },
      { name: 'Education', description: 'EdTech platforms for UAE schools and universities.' },
      { name: 'Logistics', description: 'Supply chain and fleet management systems.' },
    ],
    faqs: [
      { question: 'Do you support Arabic interfaces?', answer: 'Yes, all our solutions support RTL Arabic interfaces with native Arabic UX copywriting.' },
      { question: 'Can you work with UAE government projects?', answer: 'Yes, we deliver smart government solutions compliant with UAE digital government standards.' },
    ],
    localRate: 'AED 2,000-3,000/day',
    ourRate: '$25-45/hr',
    savings: '60-70%',
  },
};
